import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { CloudUpload as UploadIcon, Download as DownloadIcon, CheckCircle, Error as ErrorIcon, ArrowBack as ArrowBackIcon, ListAlt as ListAltIcon } from '@mui/icons-material';
import MainLayout from '../../components/layout/MainLayout';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

interface Role {
  id: string;
  code: string;
  name: string;
}

const BulkUserUpload: React.FC = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState<Role[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axios.get('/system-settings/roles');
      setRoles(response.data);
    } catch (err) {
      console.error('Error fetching roles:', err);
      setError('Failed to load roles for validation');
    }
  };

  const downloadSample = () => {
    const headers = ['firstName', 'lastName', 'email', 'mobile', 'roleCode'];
    const sampleData = ['John,Doe,john@example.com,1234567890,FACULTY'];
    const csvContent = [headers.join(','), ...sampleData].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'users_sample.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setResult(null);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post('/system-settings/users/bulk', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setResult(response.data);
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.response?.data?.message || 'Failed to upload users');
    } finally {
      setUploading(false);
    }
  };

  return (
    <MainLayout>
      <Container maxWidth="md">
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/system-settings/user-roles/users')}>
            Back
          </Button>
          <Typography variant="h4">Bulk User Upload</Typography>
        </Box>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6">1. Download Sample Template</Typography>
            <Button startIcon={<DownloadIcon />} onClick={downloadSample} variant="outlined">
              Download CSV
            </Button>
          </Box>
          <Typography variant="body2" color="textSecondary" paragraph>
            Use the sample CSV file to prepare your user data. Ensure role codes match existing roles (e.g., ADMIN, FACULTY).
          </Typography>

          <Box sx={{ my: 3 }}>
            <Typography variant="h6" gutterBottom>2. Upload File</Typography>
            <Box sx={{ border: '2px dashed #ccc', p: 3, textAlign: 'center', borderRadius: 2 }}>
              <input
                accept=".csv"
                style={{ display: 'none' }}
                id="raised-button-file"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="raised-button-file">
                <Button variant="contained" component="span" startIcon={<UploadIcon />}>
                  Select File
                </Button>
              </label>
              {file && <Typography sx={{ mt: 2 }}>{file.name}</Typography>}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpload}
              disabled={!file || uploading}
              size="large"
            >
              {uploading ? 'Processing...' : 'Upload & Process'}
            </Button>
          </Box>
          
          {uploading && <LinearProgress sx={{ mt: 2 }} />}
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {result && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Upload Results</Typography>
            <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                <ListAltIcon sx={{ mr: 1 }} />
                <Typography variant="h5">{result.success + result.failed}</Typography>
                <Typography variant="body2" sx={{ ml: 1 }}>Total Processed</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
                <CheckCircle sx={{ mr: 1 }} />
                <Typography variant="h5">{result.success}</Typography>
                <Typography variant="body2" sx={{ ml: 1 }}>Successful</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
                <ErrorIcon sx={{ mr: 1 }} />
                <Typography variant="h5">{result.failed}</Typography>
                <Typography variant="body2" sx={{ ml: 1 }}>Failed</Typography>
              </Box>
            </Box>

            {result.errors && result.errors.length > 0 && (
              <>
                <Typography variant="subtitle1" color="error" gutterBottom>Errors:</Typography>
                <List dense>
                  {result.errors.map((err: any, index: number) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <ErrorIcon color="error" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={err.email}
                        secondary={err.reason}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Paper>
        )}
      </Container>
    </MainLayout>
  );
};

export default BulkUserUpload;
